/**
 * DataQualityBanner — surfaces the gaps loudly instead of silently dropping
 * data: unattributed minutes (blank CRC), unmapped CRC names, unmapped study
 * names, in-flight/incomplete visits, manual-override totals. Deep-links to
 * the AdminPanel mapping tabs where the fixes live.
 */

import { Link } from 'react-router-dom';
import { AlertCircle } from 'lucide-react';
import { formatMinutes } from '../types';

interface DataQualityBannerProps {
  unattributedMinutes: number;
  unmappedCrcNames: string[];
  unmappedStudyNames: string[];
  inFlightCount: number;
  incompleteCount: number;
  overrideMinutes: number;
  overrideCount: number;
}

export function DataQualityBanner({
  unattributedMinutes,
  unmappedCrcNames,
  unmappedStudyNames,
  inFlightCount,
  incompleteCount,
  overrideMinutes,
  overrideCount,
}: DataQualityBannerProps) {
  const notes: React.ReactNode[] = [];

  if (unmappedCrcNames.length > 0) {
    notes.push(
      <span key="crc">
        {unmappedCrcNames.length} CRC name{unmappedCrcNames.length === 1 ? '' : 's'} not linked to a user
        ({unmappedCrcNames.slice(0, 3).join(', ')}
        {unmappedCrcNames.length > 3 ? ', …' : ''}) —{' '}
        <Link to="/status-board/admin" className="font-medium underline hover:no-underline">
          fix in Status Board Admin → CRC Mapping
        </Link>
      </span>
    );
  }
  if (unmappedStudyNames.length > 0) {
    notes.push(
      <span key="study">
        {unmappedStudyNames.length} study name{unmappedStudyNames.length === 1 ? '' : 's'} not linked to the registry —{' '}
        <Link to="/status-board/admin" className="font-medium underline hover:no-underline">
          fix in Status Board Admin → Study Mapping
        </Link>
      </span>
    );
  }
  if (unattributedMinutes > 0) {
    notes.push(
      <span key="unattributed">
        {formatMinutes(unattributedMinutes)} of visit time has no CRC recorded (shown as “Unattributed”)
      </span>
    );
  }
  if (inFlightCount > 0 || incompleteCount > 0) {
    const parts = [];
    if (inFlightCount > 0) parts.push(`${inFlightCount} still running today`);
    if (incompleteCount > 0) parts.push(`${incompleteCount} never checked out`);
    notes.push(
      <span key="inflight">
        {parts.join(' and ')} — their unfinished time is excluded from totals
      </span>
    );
  }
  if (overrideCount > 0) {
    notes.push(
      <span key="override">
        {overrideCount} visit{overrideCount === 1 ? '' : 's'} carr{overrideCount === 1 ? 'ies' : 'y'} manual
        duration overrides totaling {formatMinutes(overrideMinutes)} (not reflected in per-status totals)
      </span>
    );
  }

  if (notes.length === 0) return null;

  return (
    <div className="rounded-xl border border-amber-200 bg-amber-50 p-3 dark:border-amber-800 dark:bg-amber-900/20">
      <div className="flex gap-2">
        <AlertCircle size={16} className="mt-0.5 flex-shrink-0 text-amber-600 dark:text-amber-400" aria-hidden="true" />
        <ul className="space-y-1 text-sm text-amber-800 dark:text-amber-300">
          {notes.map((note, i) => (
            <li key={i}>{note}</li>
          ))}
        </ul>
      </div>
    </div>
  );
}

export default DataQualityBanner;
